import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { apiCall, ApiError } from '../utils/apiHelpers';
import type {
  FieldValue,
  IntakeSchemaResponse,
  IntakeSubmitResponse,
  SchemaProperty,
} from '../types';

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '0.5rem',
  border: '1px solid var(--border-color)',
  borderRadius: 4,
  background: 'var(--card-bg)',
  color: 'var(--text-color)',
};

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: 13,
  fontWeight: 500,
  marginBottom: 4,
};

export function SelfIntakePage() {
  const [searchParams] = useSearchParams();
  const t = searchParams.get('t');

  const [schema, setSchema] = useState<IntakeSchemaResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [accessError, setAccessError] = useState<boolean>(false);

  const [draft, setDraft] = useState<Record<string, FieldValue>>({});
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [result, setResult] = useState<IntakeSubmitResponse | null>(null);

  useEffect(() => {
    if (!t) {
      setLoading(false);
      setLoadError('Missing intake token.');
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      setLoadError(null);
      setAccessError(false);
      try {
        const s = await apiCall<IntakeSchemaResponse>(`/api/intake/schema?t=${encodeURIComponent(t)}`);
        if (cancelled) return;
        setSchema(s);
        const d: Record<string, FieldValue> = {};
        for (const p of s.properties) d[p.name] = p.type === 'boolean' ? false : '';
        setDraft(d);
      } catch (e) {
        if (cancelled) return;
        if (e instanceof ApiError && (e.status === 401 || e.status === 403 || e.status === 404)) {
          setAccessError(true);
        } else {
          setLoadError(e instanceof ApiError ? e.message : (e as Error).message);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [t]);

  function setField(name: string, value: FieldValue) {
    setDraft((prev) => ({ ...prev, [name]: value }));
  }

  const downloadName = useMemo(() => `intake-${Date.now()}.png`, [result]);

  async function submit(ev: React.FormEvent) {
    ev.preventDefault();
    if (!t || !schema) return;
    for (const p of schema.properties) {
      if (p.required) {
        const v = draft[p.name];
        if (v === '' || v === null || v === undefined) {
          setFormError(`"${p.label}" is required.`);
          return;
        }
      }
    }
    setFormError(null);
    setSubmitting(true);
    try {
      const fields: Record<string, FieldValue> = {};
      for (const p of schema.properties) {
        const raw = draft[p.name] ?? null;
        if (p.type === 'number') {
          fields[p.name] = raw === '' || raw === null ? null : Number(raw);
        } else if (p.type === 'boolean') {
          fields[p.name] = Boolean(raw);
        } else {
          fields[p.name] = raw === '' ? null : raw;
        }
      }
      const r = await apiCall<IntakeSubmitResponse>(
        `/api/intake/submit?t=${encodeURIComponent(t)}`,
        { method: 'POST', body: { fields } },
      );
      setResult(r);
    } catch (e) {
      setFormError(e instanceof ApiError ? e.message : (e as Error).message);
    } finally {
      setSubmitting(false);
    }
  }

  function downloadPng() {
    if (!result) return;
    const binary = atob(result.pngBase64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
    const blob = new Blob([bytes], { type: 'image/png' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = downloadName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  if (accessError) {
    return (
      <div className="signin-page">
        <div className="signin-page__card signin-page__card--error" style={{ maxWidth: 480 }}>
          <h1>Link expired</h1>
          <p>
            This intake link is no longer valid. Please contact the program staff to request a new
            link.
          </p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="signin-page">
        <div className="signin-page__card">
          <p>Loading…</p>
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="signin-page">
        <div className="signin-page__card signin-page__card--error">
          <h1>Something went wrong</h1>
          <p>{loadError}</p>
        </div>
      </div>
    );
  }

  if (!schema) return null;

  if (result) {
    return (
      <div className="signin-page">
        <div className="signin-page__card" style={{ maxWidth: 480, textAlign: 'center' }}>
          <h1 style={{ marginTop: 0 }}>Your QR code</h1>
          <p style={{ color: 'var(--text-muted)', fontSize: 14 }}>
            Take a screenshot or download this code. You will use it on subsequent visits.
          </p>
          <div style={{ display: 'flex', justifyContent: 'center', margin: '16px 0' }}>
            <img
              src={result.dataUri}
              alt="Your QR code"
              width={320}
              height={320}
              style={{
                width: 320,
                height: 320,
                imageRendering: 'pixelated',
                background: '#fff',
                padding: 8,
                borderRadius: 4,
                border: '1px solid var(--border-color)',
              }}
            />
          </div>
          <button type="button" className="btn btn--primary" onClick={downloadPng}>
            Download PNG
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="signin-page" style={{ alignItems: 'flex-start', padding: '3rem 1rem' }}>
      <div className="signin-page__card" style={{ maxWidth: 560, textAlign: 'left' }}>
        <h1 style={{ marginTop: 0 }}>{schema.name}</h1>
        <p style={{ color: 'var(--text-muted)' }}>
          Fill out the form below to get a personal QR code for this program.
        </p>
        {formError && (
          <div
            style={{
              padding: 12,
              background: '#fee',
              border: '1px solid #fcc',
              borderRadius: 6,
              marginBottom: 16,
            }}
          >
            {formError}
          </div>
        )}
        <form onSubmit={submit}>
          {schema.properties.map((p) => (
            <div key={p.name} style={{ marginBottom: 12 }}>
              <label style={labelStyle}>
                {p.label}
                {p.required ? ' *' : ''}
              </label>
              <FieldInput property={p} value={draft[p.name]} onChange={(v) => setField(p.name, v)} />
            </div>
          ))}
          <button type="submit" className="btn btn--primary signin-page__button" disabled={submitting}>
            {submitting ? 'Submitting…' : 'Get my QR code'}
          </button>
        </form>
      </div>
    </div>
  );
}

function FieldInput({
  property,
  value,
  onChange,
}: {
  property: SchemaProperty;
  value: FieldValue | undefined;
  onChange: (v: FieldValue) => void;
}) {
  switch (property.type) {
    case 'number':
      return (
        <input
          type="number"
          value={value === null || value === undefined ? '' : String(value)}
          onChange={(e) => onChange(e.target.value)}
          style={inputStyle}
        />
      );
    case 'date':
      return (
        <input
          type="date"
          value={value === null || value === undefined ? '' : String(value)}
          onChange={(e) => onChange(e.target.value)}
          style={inputStyle}
        />
      );
    case 'boolean':
      return (
        <input
          type="checkbox"
          checked={Boolean(value)}
          onChange={(e) => onChange(e.target.checked)}
        />
      );
    case 'select':
      return (
        <select
          value={value === null || value === undefined ? '' : String(value)}
          onChange={(e) => onChange(e.target.value)}
          style={inputStyle}
        >
          <option value="">— Select —</option>
          {(property.options ?? []).map((opt) => (
            <option key={opt} value={opt}>
              {opt}
            </option>
          ))}
        </select>
      );
    default:
      return (
        <input
          type="text"
          value={value === null || value === undefined ? '' : String(value)}
          onChange={(e) => onChange(e.target.value)}
          style={inputStyle}
        />
      );
  }
}
